'use client';

import { useState } from 'react';
import PropTypes from 'prop-types';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Pagination, Thumbs } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import '@/styles/portfolio-gallery.css';
import CreditedImage from '@/components/website/CreditedImage';
import { normalizeGalleryImageList } from '@/utils/galleryImage';

const THUMB_BREAKPOINTS = {
  0: { slidesPerView: 2.5, spaceBetween: 8 },
  600: { slidesPerView: 3.5, spaceBetween: 10 },
  900: { slidesPerView: 4, spaceBetween: 10 },
};

const galleryImageShape = PropTypes.oneOfType([
  PropTypes.string.isRequired,
  PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    photographerCredit: PropTypes.string,
  }),
]);

function GalleryOpenButton({ src, imageLabel }) {
  if (!src) {
    return null;
  }

  return (
    <IconButton
      component="a"
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${imageLabel} in a new tab`}
      className="portfolio-gallery-open-btn"
      size="small"
    >
      <OpenInNewIcon fontSize="small" />
    </IconButton>
  );
}

GalleryOpenButton.propTypes = {
  src: PropTypes.string,
  imageLabel: PropTypes.string.isRequired,
};

function GallerySlide({ src, alt, photographerCredit }) {
  return (
    <Box className="portfolio-gallery-slide">
      <CreditedImage
        src={src}
        alt={alt}
        photographerCredit={photographerCredit}
        imgClassName="portfolio-gallery-slide-img"
        draggable={false}
      />
    </Box>
  );
}

GallerySlide.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  photographerCredit: PropTypes.string,
};

function ThumbsGallery({ images }) {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleMainSlideChange = swiper => {
    setActiveIndex(swiper.realIndex);
  };

  return (
    <Box className="portfolio-gallery">
      <Box className="portfolio-gallery-main-wrap">
        <GalleryOpenButton
          src={images[activeIndex]?.src}
          imageLabel={`portfolio image ${activeIndex + 1} full size`}
        />
        <Swiper
          className="portfolio-gallery-main"
          pagination={{
            type: 'fraction',
          }}
          modules={[Pagination, Navigation, Thumbs]}
          navigation
          loop={images.length > 1}
          spaceBetween={0}
          slidesPerView={1}
          speed={450}
          preventInteractionOnTransition
          onSwiper={handleMainSlideChange}
          onSlideChange={handleMainSlideChange}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
        >
          {images.map((image, index) => (
            <SwiperSlide key={`${image.src}-${index}`}>
              <GallerySlide
                src={image.src}
                alt={image.alt}
                photographerCredit={image.photographerCredit}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <Swiper
        className="portfolio-gallery-thumbs"
        modules={[FreeMode, Thumbs]}
        onSwiper={setThumbsSwiper}
        spaceBetween={10}
        slidesPerView={4}
        breakpoints={THUMB_BREAKPOINTS}
        freeMode
        watchSlidesProgress
        watchOverflow
      >
        {images.map((image, index) => (
          <SwiperSlide key={`thumb-${image.src}-${index}`}>
            <Box className="portfolio-gallery-thumb">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={image.src} alt="" aria-hidden draggable={false} />
              <span className="portfolio-gallery-thumb-label">View image {index + 1}</span>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}

ThumbsGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      photographerCredit: PropTypes.string,
    }),
  ).isRequired,
};

export default function PortfolioProjectGallery({ images }) {
  const normalized = normalizeGalleryImageList(images, index => `Portfolio image ${index + 1}`);
  if (!normalized.length) return null;

  if (normalized.length === 1) {
    const image = normalized[0];
    return (
      <Box className="portfolio-gallery portfolio-gallery--single">
        <Box className="portfolio-gallery-main-wrap">
          <GalleryOpenButton src={image.src} imageLabel="portfolio image full size" />
          <GallerySlide
            src={image.src}
            alt={image.alt || 'Portfolio image'}
            photographerCredit={image.photographerCredit}
          />
        </Box>
      </Box>
    );
  }

  return <ThumbsGallery images={normalized} />;
}

PortfolioProjectGallery.propTypes = {
  images: PropTypes.arrayOf(galleryImageShape).isRequired,
};
